import { useState, useRef } from 'react';
import { memberLogin, adminVerify } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X, Mail, Loader2 } from 'lucide-react';

export default function AuthModal({ onClose }) {
  const [step, setStep] = useState('email'); // email | admin-otp
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const otpRefs = useRef([]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await memberLogin(email.trim().toLowerCase());
      const data = res.data;

      if (data.status === 'otp_required') {
        // Admin needs OTP
        setStep('admin-otp');
        toast.info('Admin login — enter your OTP code');
      } else {
        // Member or admin logged in directly
        login(data.user, data.token);
        toast.success(data.user.role === 'admin' ? 'Welcome, Admin!' : 'Welcome to Bunchh!');
        onClose();
        navigate(data.user.role === 'admin' ? '/admin' : '/members');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Access denied');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminVerify = async () => {
    const code = otp.join('');
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const res = await adminVerify(email.trim().toLowerCase(), code);
      login(res.data.user, res.data.token);
      toast.success('Welcome, Admin!');
      onClose();
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
    if (newOtp.every(d => d !== '') && newOtp.join('').length === 6) {
      setTimeout(() => handleAdminVerify(), 100);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="auth-modal">
      <div className="modal-box">
        <button className="modal-close" onClick={onClose} data-testid="auth-modal-close"><X size={20} /></button>
        <div className="modal-eyebrow">Member Access</div>

        {step === 'email' ? (
          <>
            <h2 className="modal-title">Enter your email</h2>
            <p className="modal-sub">Enter the email address approved by the Bunchh team.</p>
            <form onSubmit={handleEmailLogin}>
              <div className="form-group">
                <label className="form-label">Email address</label>
                <div style={{ position: 'relative' }}>
                  <Mail size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    className="form-input"
                    style={{ paddingLeft: 40 }}
                    type="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="auth-email-input"
                  />
                </div>
              </div>
              <button
                className="btn-coral"
                type="submit"
                disabled={loading}
                style={{ width: '100%', marginTop: 8 }}
                data-testid="auth-login-btn"
              >
                {loading ? <Loader2 size={18} className="animate-spin" style={{ display: 'inline' }} /> : 'Continue'}
              </button>
            </form>
          </>
        ) : (
          <>
            <h2 className="modal-title">Admin verification</h2>
            <p className="modal-sub">Enter the 6-digit code for <strong>{email}</strong></p>
            <div className="otp-input-group" data-testid="otp-input-group">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  autoFocus={i === 0}
                  data-testid={`otp-input-${i}`}
                />
              ))}
            </div>
            <button
              className="btn-coral"
              onClick={handleAdminVerify}
              disabled={loading || otp.join('').length !== 6}
              style={{ width: '100%' }}
              data-testid="auth-verify-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" style={{ display: 'inline' }} /> : 'Verify & Login'}
            </button>
            <button
              onClick={() => { setStep('email'); setOtp(['', '', '', '', '', '']); }}
              style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer' }}
              data-testid="auth-back-btn"
            >
              Use a different email
            </button>
          </>
        )}
      </div>
    </div>
  );
}
